// Librairies
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

// Interfaces
import { ForecastMeta, PoissonMeta } from 'src/app/interfaces/api/chart';

// Components
import { ChartComponent } from 'src/app/components/project-pages/dashboard/widgets/chart/chart.component';
import { ChipTone, chipTone } from 'src/app/components/project-pages/dashboard/widgets/table/table.component';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { ForecastNoteComponent } from 'src/app/components/project-pages/dashboard/forecast-note/forecast-note.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';

export type InsightSource = 'medianDays' | 'meanDays' | 'last' | 'total' | 'trend';

// Left panel blocks, in widget.design.insight.
export interface InsightDesign {
  figure?: { enabled?: boolean; source?: InsightSource; label?: string };
  probabilities?: { enabled?: boolean };
  ranking?: { enabled?: boolean; top?: number | null; warning?: number | null; critical?: number | null; higherIsWorse?: boolean };
  note?: { enabled?: boolean; text?: string };
}

export interface InsightValues {
  meanDays: number | null;
  // Poisson: a 50 % chance of the next event before medianDays (dueDate), its range from the rate's, the rate's trend.
  medianDays: number | null;
  delayRange: [number, number] | null;
  riskTrend: PoissonMeta['trend'] | null;
  dueDate: string | null;
  trendPct: number | null;
  total: number | null;
  last: number | null;
  probabilities: { days: number; percent: number }[];   // empty without Poisson
  // What the probabilities rest on: the events counted over the history, and whether they came in waves.
  rate: { events: number; days: number; waves: boolean } | null;
}

export interface InsightRank {
  name: string;
  value: number;
  tone: ChipTone;
}

const PROBABILITY_DAYS = [7, 30, 90];
export const DEFAULT_RANKING_TOP = 5;
export const INSIGHT_NOTE_VARIABLES = ['medianDays', 'delayMin', 'delayMax', 'meanDays', 'dueDate', 'trendPct', 'total'] as const;
export const INSIGHT_NOTE_PRESETS = ['delay', 'probable_date', 'trend', 'total'] as const;

// A delay in days, readable: in days, in hours under a day ("0 days" for an event every few minutes says nothing).
export function delayParts(days: number): { key: string; count: number } {
  if (days >= 1) return { key: 'editor.insight_days', count: Math.round(days) };
  const hours = days * 24;
  return hours >= 1 ? { key: 'editor.insight_hours', count: Math.round(hours) } : { key: 'editor.insight_under_hour', count: 1 };
}

// The last complete bin, every series added up (the bin in progress, partial, is not a value yet).
function lastValue(history: any[]): number | null {
  const complete = history.filter(row => !row.partial);
  if (!complete.length) return null;
  const lastX = complete.reduce((max, row) => (`${row.x}` > max ? `${row.x}` : max), `${complete[0].x}`);
  return complete.filter(row => `${row.x}` === lastX).reduce((sum, row) => sum + Number(row.y), 0);
}

// What the panel can show, from the rows and the forecast meta; null when the data is missing.
export function insightValues(rows: any[], meta: ForecastMeta | null): InsightValues {
  const history = (rows ?? []).filter(row => row.kind !== 'forecast' && Number.isFinite(Number(row.y)));
  const series = meta?.series?.find(entry => !entry.reason) ?? null;
  const poisson = series?.poisson;
  const [fast, slow] = poisson?.meanDaysRange ?? [null, null];

  return {
    meanDays: typeof poisson?.meanDays === 'number' ? poisson.meanDays : null,
    medianDays: typeof poisson?.medianDays === 'number' ? poisson.medianDays : null,
    delayRange: fast !== null && slow !== null ? [fast * Math.LN2, slow * Math.LN2] : null,
    riskTrend: poisson?.trend ?? null,
    dueDate: poisson?.dueDate ?? null,
    trendPct: typeof series?.trendPct === 'number' ? series.trendPct : null,
    total: history.length ? history.reduce((sum, row) => sum + Number(row.y), 0) : null,
    last: lastValue(history),
    probabilities: poisson?.p ? PROBABILITY_DAYS.filter(days => typeof poisson.p![days] === 'number').map(days => ({ days, percent: poisson.p![days]! * 100 })) : [],
    rate: poisson ? { events: poisson.events, days: poisson.days, waves: (series?.quality?.dispersion ?? 1) > 1.5 } : null,
  };
}

// The note with its variables replaced; a missing one becomes "—".
export function insightNote(text: string, values: InsightValues): string {
  const round = (value: number | null | undefined) => (value === null || value === undefined ? null : Math.round(value));
  const variables: Record<string, number | string | null> = {
    meanDays: round(values.meanDays),
    medianDays: round(values.medianDays),
    delayMin: round(values.delayRange?.[0]),
    delayMax: round(values.delayRange?.[1]),
    dueDate: values.dueDate,
    trendPct: values.trendPct === null ? null : Math.round(values.trendPct),
    total: values.total,
  };
  return text.replace(new RegExp(String.raw`\{(${INSIGHT_NOTE_VARIABLES.join('|')})\}`, 'g'), (_match, name: string) => `${variables[name] ?? '—'}`);
}

// The ranking query: the same collection, filters and aggregation, one total per project (no X axis, no forecast).
// Without its collection the API falls back on Track, the largest one: a ranking of 20 s, most projects timing out.
export function rankingQuery(query: any, collection?: string): any {
  const { field, forecast, compare, series, ...rest } = query ?? {};
  delete rest['x-axis'];
  return { ...rest, collection: collection ?? rest.collection, byProject: true };
}

export function insightRanking(rows: any[], settings: InsightDesign['ranking'] = {}): InsightRank[] {
  const top = settings?.top && settings.top > 0 ? settings.top : DEFAULT_RANKING_TOP;
  return (rows ?? [])
    .map(row => ({ name: `${row.p ?? ''}`, value: Number(row.y) || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, top)
    .map(rank => ({
      ...rank,
      tone: chipTone(rank.value, { format: 'chip', warning: settings?.warning ?? undefined, critical: settings?.critical ?? undefined, higherIsWorse: settings?.higherIsWorse }),
    }));
}

// Insight: the chart (and its forecast) on the right, key figures on the left. A chart, so its query and rendering are the chart's.
@Component({
  selector: 'app-insight',
  standalone: true,
  imports: [
    ForecastNoteComponent,
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './insight.component.html',
  styleUrl: './insight.component.scss'
})

export class InsightComponent extends ChartComponent {
  // Variables
  values: InsightValues = insightValues([], null);
  ranking: InsightRank[] = [];
  // The totals per project as loaded: the ranking follows its settings (top, thresholds) without a new query.
  private rankingRows: any[] = [];
  note: string = '';

  get insight(): InsightDesign {
    return this.widget?.design?.insight ?? {};
  }

  override init(): void {
    super.init();
    this.values = insightValues(this.widget?.data ?? [], this.forecastMeta);
    this.note = insightNote(this.insight.note?.text ?? '', this.values);
    this.ranking = insightRanking(this.rankingRows, this.insight.ranking);
  }

  delayText(days: number): string {
    const { key, count } = delayParts(days);
    return this.translate.instant(key, { count });
  }

  // What a probability bar means, on hover.
  probabilityHint(probability: { days: number; percent: number }): string {
    const rate = this.values.rate;
    if (!rate) return '';
    const params = { percent: Math.round(probability.percent), days: probability.days, events: rate.events, period: Math.round(rate.days) };
    return [
      this.translate.instant('editor.insight_probability_hint', params),
      rate.waves ? this.translate.instant('editor.insight_probability_waves') : '',
    ].filter(Boolean).join(' ');
  }

  override updateData(): void {
    super.updateData();
    this.loadRanking();
  }

  // Synthesis only: a second query, one total per project of the tenant.
  private loadRanking(): void {
    this.rankingRows = [];
    this.ranking = [];
    if (!this.tenantId || !this.insight.ranking?.enabled || !this.widget?.query) return;

    this.chartService.aggregateTenant(this.tenantId, this.tenantQuery(rankingQuery(this.widget.query, this.widget.category)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.rankingRows = result.rows ?? [];
          this.ranking = insightRanking(this.rankingRows, this.insight.ranking);
          this.cdr.detectChanges();
        },
        error: () => this.ranking = []
      });
  }
}
